/**
 * Worksheets: 12 problems; every worksheet includes the 11th and 12th facts; revisit facts get slipped in;
 * a fact missed twice shows the answer, the student types it, and it comes back at the end.
 * A stage passes at 10 of 12 correct on the first try.
 */
import { SETTINGS, UNITS, type Unit, factKey, parseKey, isDiv } from "./curriculum";
import { revisitIn, type StudentProgress } from "./progress";

export type Problem = [number, number];

export interface Worksheet {
  items: Problem[];
  /** Problems in the original sheet (before recycled facts are appended). */
  orig: number;
  i: number;
  firstTry: number;
  fluent: number;
  recycled: Set<string>;
}

const mkShuffle = (rng: () => number) => <T>(arr: T[]): T[] => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
};

export function buildWorksheet(u: Unit, p: StudentProgress, rng: () => number = Math.random): Worksheet {
  const shuffle = mkShuffle(rng);
  let items: Problem[] = [];
  if (u.special) {
    for (const t of u.tables) items.push([t, 11], [t, 12]);
    items = shuffle(items.concat(shuffle([...items]))).slice(0, SETTINGS.SHEET);
  } else if (u.mixed) {
    // Mixed division: every divisor, no 0, revisit facts first.
    let guard = 0;
    for (const k of shuffle(revisitIn(p, u.tables, "div")).slice(0, 4)) items.push(parseKey(k));
    while (items.length < SETTINGS.SHEET && guard++ < 500) {
      const a = u.tables[Math.floor(rng() * u.tables.length)]!, b = 1 + Math.floor(rng() * 12);
      if (!items.some((x) => x[0] === a && x[1] === b)) items.push([a, b]);
    }
    items = shuffle(items);
  } else if (u.tables.length === 1 && u.tables[0] === 12) {
    // The 12s lesson (Meliesa, Oct 29): mixed practice of ×1–4 (begin with your number), the skipped line, then ×6–10
    // (begin with one more), then the 11th and 12th. Division by 12 keeps the same shape. ORDERED.
    items = shuffle([[12, 1], [12, 2], [12, 3], [12, 4]] as Problem[]).concat([[12, 5]], shuffle([[12, 6], [12, 7], [12, 8], [12, 9], [12, 10]] as Problem[]), [[12, 11], [12, 12]]);
  } else {
    const lo = isDiv(u) ? 1 : 0; // no 0 ÷ 9 problems
    for (const t of u.tables) for (let b = lo; b <= 12; b++) items.push([t, b]);
    const must = items.filter((x) => x[1] >= 11);
    const rest = shuffle(items.filter((x) => x[1] < 11));
    items = shuffle(must.concat(rest.slice(0, Math.max(0, SETTINGS.SHEET - must.length))));
  }
  for (const k of shuffle(revisitIn(p, u.tables, isDiv(u) ? "div" : "mul"))) {
    const [a, b] = parseKey(k);
    if (!u.mixed && items.length < SETTINGS.SHEET + 2 && !items.some((x) => x[0] === a && x[1] === b)) items.splice(Math.floor(rng() * items.length), 0, [a, b]);
  }
  return { items, orig: items.length, i: 0, firstTry: 0, fluent: 0, recycled: new Set() };
}

/** Mixed review: every unit through the current one, revisit facts first, 15 problems, no tools. */
export function buildReview(currentUnitId: string, p: StudentProgress, rng: () => number = Math.random): Worksheet {
  const shuffle = mkShuffle(rng);
  const idx = UNITS.findIndex((u) => u.id === currentUnitId);
  const tables = UNITS.slice(0, idx + 1).filter((x) => !isDiv(x)).flatMap((x) => x.tables);
  const items: Problem[] = shuffle(revisitIn(p, tables)).slice(0, 6).map(parseKey);
  let guard = 0;
  while (items.length < 15 && guard++ < 500) {
    const a = tables[Math.floor(rng() * tables.length)]!;
    const b = Math.floor(rng() * 13);
    if (!items.some((x) => x[0] === a && x[1] === b)) items.push([a, b]);
  }
  const sh = shuffle(items);
  return { items: sh, orig: sh.length, i: 0, firstTry: 0, fluent: 0, recycled: new Set() };
}

/** After a second miss, the fact is appended to the end of the sheet once. */
export function recycle(ws: Worksheet, a: number, b: number): void {
  const k = factKey(a, b);
  if (ws.i >= ws.orig) return; // already a recycled problem
  if (ws.recycled.has(k)) return;
  ws.recycled.add(k);
  ws.items.push([a, b]);
}

export const isRecycled = (ws: Worksheet): boolean => ws.i >= ws.orig;
export const passed = (ws: Worksheet): boolean => ws.firstTry >= SETTINGS.PASS;
